import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { storeMcpRecommendations, getStoredMcpRecommendations } from "../../../utils/recsCache";
import { requestRecommendations } from "../../../utils/requestRecommendations";
import { getRiskLevelPriorityColors } from "../../../utils/riskLevelUtils";
import { levelDisplayNames } from "../../../utils/displayNameUtils";
import { subcategoryLabelsMap } from "../../../utils/categoryLabels";

const getAttackTypeLabel = (key) =>
  subcategoryLabelsMap[key] ||
  (key && key.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase())) ||
  key;

const sectionCardShellStyle = {
  background: "linear-gradient(145deg, var(--card-bg), rgba(59,130,246,0.05))",
  borderRadius: "16px",
  border: "1px solid var(--card-border)",
  boxShadow: "0 12px 30px rgba(15,23,42,0.08)",
};

const SectionCardIcon = ({ icon }) => (
  <div
    style={{
      width: "40px",
      height: "40px",
      borderRadius: "12px",
      background: "rgba(59,130,246,0.15)",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      fontSize: "20px",
      color: "#3b82f6",
      flexShrink: 0,
    }}
  >
    {icon}
  </div>
);

const SectionCard = ({ children, title, icon }) => (
  <div
    style={{
      ...sectionCardShellStyle,
      padding: "24px",
      display: "flex",
      flexDirection: "column",
      gap: "16px",
    }}
  >
    <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
      <SectionCardIcon icon={icon} />
      <h3 style={{ margin: 0, color: "var(--card-text)", fontSize: "18px" }}>{title}</h3>
    </div>
    {children}
  </div>
);

const SectionCardAccordion = ({ children, title, icon, defaultOpen = false }) => {
  const [isOpen, setIsOpen] = useState(defaultOpen);

  return (
    <div style={{ ...sectionCardShellStyle, overflow: "hidden" }}>
      <button
        type="button"
        onClick={() => setIsOpen((open) => !open)}
        aria-expanded={isOpen}
        style={{
          width: "100%",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: "12px",
          padding: "24px",
          background: "transparent",
          border: "none",
          cursor: "pointer",
          textAlign: "left",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: "12px", minWidth: 0 }}>
          <SectionCardIcon icon={icon} />
          <h3 style={{ margin: 0, color: "var(--card-text)", fontSize: "18px" }}>{title}</h3>
        </div>
        <i
          className={`fas fa-chevron-${isOpen ? "up" : "down"}`}
          style={{ color: "#64748b", fontSize: "14px", flexShrink: 0 }}
          aria-hidden="true"
        />
      </button>
      {isOpen && (
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: "16px",
            padding: "0 24px 24px",
          }}
        >
          {children}
        </div>
      )}
    </div>
  );
};

const McpServerTestsReportTabRecommendations = ({ reportData }) => {
  const { t } = useTranslation("reports_recs", { keyPrefix: "recs.mcp" });
  const [recommendationDetails, setRecommendationDetails] = useState(() => {
    
    const reportId = reportData?.id;
    return reportId ? getStoredMcpRecommendations(reportId) : null;
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);


  // uncomment in prod
  useEffect(() => {
    const reportId = reportData?.id;
    if (!reportId) {
      setRecommendationDetails(null);
      return;
    }

    const stored = getStoredMcpRecommendations(reportId);
    if (stored) {
      setRecommendationDetails(stored);
    } else {
      setRecommendationDetails(null);
    }
  }, [reportData?.id]);
  // uncomment in prod

  const fetchRecommendations = async () => {
    try {
      setLoading(true);
      setError(null);

      // Get report ID from reportData
      const reportId = reportData?.id;
      if (!reportId) {
        throw new Error(t("reportIdNotFound"));
      }

      const result = await requestRecommendations({
        recommFor: "mcp",
        reportId,
        reportData,
        fallbackMessage: t("noRecommendationsAvailable"),
      });
      setRecommendationDetails(result);
      
      if (result && Object.keys(result).length > 0) {
        storeMcpRecommendations(reportId, result);
      }
    } catch (err) {
      console.error("Error fetching recommendations:", err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Show button to load recommendations if not loaded yet
  if (!recommendationDetails && !loading) {
    return (
      <div style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        padding: "60px 40px",
        background: "var(--card-bg)",
        borderRadius: "12px",
        border: "1px solid var(--card-border)",
        marginTop: "20px"
      }}>
        <div style={{
          width: "64px",
          height: "64px",
          borderRadius: "16px",
          background: "rgba(59, 130, 246, 0.15)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          marginBottom: "20px"
        }}>
          <i className="fas fa-lightbulb" style={{ fontSize: "28px", color: "#3b82f6" }}></i>
        </div>
      
        {error && (
          <div style={{ 
            fontSize: "14px", 
            color: "#ef4444", 
            marginBottom: "20px",
            padding: "12px 20px",
            background: "rgba(239, 68, 68, 0.1)",
            borderRadius: "8px",
            border: "1px solid rgba(239, 68, 68, 0.2)"
          }}>
            {t("noRecommendationsFoundTitle")}
          </div>
        )}
        <button
          onClick={fetchRecommendations}
          disabled={loading}
          style={{
            padding: "12px 32px",
            background: "linear-gradient(135deg, #3b82f6, #1d4ed8)",
            color: "white",
            border: "none",
            borderRadius: "8px",
            fontSize: "16px",
            fontWeight: "600",
            cursor: loading ? "not-allowed" : "pointer",
            transition: "all 0.3s ease",
            boxShadow: "0 4px 12px rgba(59, 130, 246, 0.3)",
            opacity: loading ? 0.6 : 1
          }}
          onMouseOver={(e) => {
            if (!loading) {
              e.target.style.transform = "translateY(-2px)";
              e.target.style.boxShadow = "0 6px 16px rgba(59, 130, 246, 0.4)";
            }
          }}
          onMouseOut={(e) => {
            if (!loading) {
              e.target.style.transform = "translateY(0)";
              e.target.style.boxShadow = "0 4px 12px rgba(59, 130, 246, 0.3)";
            }
          }}
        >
          {loading ? (
            <>
              <i className="fas fa-spinner fa-spin" style={{ marginRight: "8px" }}></i>
              {t("loading")}
            </>
          ) : (
            <>
              <i className="fas fa-download" style={{ marginRight: "8px" }}></i>
              {t("loadRecommendations")}
            </>
          )}
        </button>
      </div>
    );
  }

  // Show loading state
  if (loading) {
    return (
      <div style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        padding: "60px 40px",
        background: "var(--card-bg)",
        borderRadius: "12px",
        border: "1px solid var(--card-border)",
        marginTop: "20px"
      }}>
        <div style={{
          width: "64px",
          height: "64px",
          borderRadius: "16px",
          background: "rgba(59, 130, 246, 0.15)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          marginBottom: "20px"
        }}>
          <i className="fas fa-spinner fa-spin" style={{ fontSize: "28px", color: "#3b82f6" }}></i>
        </div>
        <div style={{ fontSize: "18px", color: "var(--card-text)", fontWeight: 600 }}>
          {t("loadingRecommendations")}
        </div>
      </div>
    );
  }

  // Show error if recommendations failed to load
  if (error && !recommendationDetails) {
    return (
      <div style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        padding: "60px 40px",
        background: "var(--card-bg)",
        borderRadius: "12px",
        border: "1px solid var(--card-border)",
        marginTop: "20px"
      }}>
        <div style={{
          width: "64px",
          height: "64px",
          borderRadius: "16px",
          background: "rgba(239, 68, 68, 0.15)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          marginBottom: "20px"
        }}>
          <i className="fas fa-exclamation-triangle" style={{ fontSize: "28px", color: "#ef4444" }}></i>
        </div>
        <div style={{ fontSize: "18px", color: "var(--card-text)", fontWeight: 600, marginBottom: "20px" }}>
          {t("noRecommendationsFoundTitle")}
        </div>
        <button
          onClick={fetchRecommendations}
          style={{
            padding: "12px 32px",
            background: "linear-gradient(135deg, #3b82f6, #1d4ed8)",
            color: "white",
            border: "none",
            borderRadius: "8px",
            fontSize: "16px",
            fontWeight: "600",
            cursor: "pointer",
            transition: "all 0.3s ease",
            boxShadow: "0 4px 12px rgba(59, 130, 246, 0.3)"
          }}
          onMouseOver={(e) => {
            e.target.style.transform = "translateY(-2px)";
            e.target.style.boxShadow = "0 6px 16px rgba(59, 130, 246, 0.4)";
          }}
          onMouseOut={(e) => {
            e.target.style.transform = "translateY(0)";
            e.target.style.boxShadow = "0 4px 12px rgba(59, 130, 246, 0.3)";
          }}
        >
          <i className="fas fa-redo" style={{ marginRight: "8px" }}></i>
          {t("retry")}
        </button>
      </div>
    );
  }

  // Show empty state if recommendations loaded but empty
  if (recommendationDetails && Object.keys(recommendationDetails).length === 0) {
    return (
      <div style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        padding: "60px 40px",
        background: "var(--card-bg)",
        borderRadius: "12px",
        border: "1px solid var(--card-border)",
        marginTop: "20px"
      }}>
        <div style={{
          width: "64px",
          height: "64px",
          borderRadius: "16px",
          background: "rgba(107, 114, 128, 0.15)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          marginBottom: "20px"
        }}>
          <i className="fas fa-compass" style={{ fontSize: "28px", color: "#6b7280" }}></i>
        </div>
        <div style={{ fontSize: "18px", color: "var(--card-text)", fontWeight: 600 }}>
          {t("noRecommendationsAvailable")}
        </div>
      </div>
    );
  }

  const {
    executive_summary,
    security_assessment,
    critical_recommendations,
    high_priority_recommendations,
    medium_priority_recommendations,
    monitoring_recommendations,
    compliance_recommendations,
    implementation_roadmap,
    data_quality_warnings,
    metadata,
  } = recommendationDetails;

  const overallRisk = executive_summary?.overall_risk || "medium";
  const priorityStyle = getRiskLevelPriorityColors(overallRisk?.toLowerCase() || "medium");
  const tableHeaderStyle = {
    padding: "14px 16px",
    textAlign: "left",
    fontWeight: 600,
    fontSize: "12px",
    textTransform: "uppercase",
    letterSpacing: "0.05em",
    color: "var(--muted-text)",
    background: "rgba(59, 130, 246, 0.08)",
    borderBottom: "1px solid var(--card-border)",
  };
  const summary = executive_summary?.summary;
  const monitoringCount = monitoring_recommendations?.length || 0;

  const renderRecommendationCard = (item, i) => (
    <div
      key={i}
      style={{
        border: "1px solid var(--card-border)",
        borderRadius: "14px",
        overflow: "hidden",
        background: "var(--card-bg)",
      }}
    >
      <div
        style={{
          padding: "16px 20px",
          background: "rgba(0, 0, 0, 0.02)",
          borderBottom: "1px solid var(--card-border)",
          display: "flex",
          alignItems: "flex-start",
          gap: "12px",
        }}
      >
        <div
          style={{
            width: "32px",
            height: "32px",
            borderRadius: "8px",
            background: "rgba(0, 0, 0, 0.06)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
          }}
        >
          <i className="fas fa-exclamation-triangle" style={{ fontSize: "14px", color: "var(--card-text)", opacity: 0.7 }}></i>
        </div>
        <div style={{ flex: 1, display: "flex", flexDirection: "column", gap: "6px" }}>
          <div style={{ display: "flex", alignItems: "center", gap: "12px", flexWrap: "wrap" }}>
            <span style={{ fontSize: "16px", fontWeight: 600, color: "var(--card-text)" }}>
              {item.title || getAttackTypeLabel(item.category)}
            </span>
            {item.priority && (
              <span
                style={{
                  padding: "4px 10px",
                  borderRadius: "999px",
                  fontSize: "11px",
                  fontWeight: 600,
                  color: "white",
                  background: getRiskLevelPriorityColors(item.priority?.toLowerCase()).gradient,
                }}
              >
                {levelDisplayNames[item.priority?.toLowerCase()] || item.priority} {t("priority")}
              </span>
            )}
          </div>
          {item.description && (
            <div style={{ fontSize: "13px", color: "var(--card-text)", lineHeight: 1.5 }}>{item.description}</div>
          )}
          {item.note && (
            <div style={{ fontSize: "11px", color: "#9ca3af", lineHeight: 1.4 }}>{item.note}</div>
          )}
        </div>
      </div>
      <div style={{ padding: "20px", display: "flex", flexDirection: "column", gap: "16px" }}>
        {(item.success_rate != null || item.failed_requests != null || item.total_requests != null || item.failure_rate != null || item.readiness != null) && (
          <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: "10px" }}>
            {item.success_rate != null && (
              <div style={{ background: "rgba(34, 197, 94, 0.08)", borderRadius: "8px", padding: "12px", textAlign: "center", border: "1px solid rgba(34, 197, 94, 0.2)" }}>
                <div style={{ fontSize: "10px", textTransform: "uppercase", letterSpacing: "0.06em", color: "#16a34a", fontWeight: 600 }}>{t("successRate")}</div>
                <div style={{ fontSize: "16px", fontWeight: 700, color: "var(--card-text)", marginTop: "4px" }}>{item.success_rate}</div>
              </div>
            )}
            {item.failed_requests != null && (
              <div style={{ background: "rgba(239, 68, 68, 0.08)", borderRadius: "8px", padding: "12px", textAlign: "center", border: "1px solid rgba(239, 68, 68, 0.2)" }}>
                <div style={{ fontSize: "10px", textTransform: "uppercase", letterSpacing: "0.06em", color: "#dc2626", fontWeight: 600 }}>{t("failed")}</div>
                <div style={{ fontSize: "16px", fontWeight: 700, color: "var(--card-text)", marginTop: "4px" }}>{item.failed_requests}</div>
              </div>
            )}
            {item.total_requests != null && (
              <div style={{ background: "rgba(59, 130, 246, 0.08)", borderRadius: "8px", padding: "12px", textAlign: "center", border: "1px solid rgba(59, 130, 246, 0.2)" }}>
                <div style={{ fontSize: "10px", textTransform: "uppercase", letterSpacing: "0.06em", color: "#2563eb", fontWeight: 600 }}>{t("totalRequests")}</div>
                <div style={{ fontSize: "16px", fontWeight: 700, color: "var(--card-text)", marginTop: "4px" }}>{item.total_requests}</div>
              </div>
            )}
            {item.failure_rate != null && (
              <div style={{ background: "rgba(0, 0, 0, 0.04)", borderRadius: "8px", padding: "12px", textAlign: "center", border: "1px solid var(--card-border)" }}>
                <div style={{ fontSize: "10px", textTransform: "uppercase", letterSpacing: "0.06em", color: "var(--muted-text)", fontWeight: 600 }}>{t("failureRate")}</div>
                <div style={{ fontSize: "16px", fontWeight: 700, color: "var(--card-text)", marginTop: "4px" }}>{item.failure_rate}</div>
              </div>
            )}
            {item.readiness != null && (
              <div style={{ background: "rgba(0, 0, 0, 0.04)", borderRadius: "8px", padding: "12px", textAlign: "center", border: "1px solid var(--card-border)" }}>
                <div style={{ fontSize: "10px", textTransform: "uppercase", letterSpacing: "0.06em", color: "var(--muted-text)", fontWeight: 600 }}>{t("readiness")}</div>
                <div style={{ fontSize: "16px", fontWeight: 700, color: "var(--card-text)", marginTop: "4px" }}>{item.readiness}</div>
              </div>
            )}
          </div>
        )}
        {item.recommendation && (
          <div style={{ background: "rgba(0, 0, 0, 0.04)", borderRadius: "12px", padding: "16px", border: "1px solid var(--card-border)" }}>
            <div style={{ fontSize: "12px", textTransform: "uppercase", letterSpacing: "0.08em", color: "var(--muted-text)", marginBottom: "10px", fontWeight: 600 }}>{t("recommendation")}</div>
            <div style={{ fontSize: "14px", color: "var(--card-text)", lineHeight: 1.5 }}>{item.recommendation}</div>
          </div>
        )}
        {item.technical_implementation?.length > 0 && (
          <div style={{ background: "rgba(0, 0, 0, 0.04)", borderRadius: "12px", padding: "16px", border: "1px solid var(--card-border)" }}>
            <div style={{ fontSize: "12px", textTransform: "uppercase", letterSpacing: "0.08em", color: "var(--muted-text)", marginBottom: "10px", fontWeight: 600 }}>{t("implementation")}</div>
            <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
              {item.technical_implementation.map((step, j) => (
                <div key={j} style={{ display: "flex", alignItems: "flex-start", gap: "8px", fontSize: "14px", color: "var(--card-text)" }}>
                  <i className="fas fa-circle" style={{ fontSize: "6px", color: "var(--muted-text)", marginTop: "7px", flexShrink: 0 }}></i>
                  {step}
                </div>
              ))}
            </div>
          </div>
        )}
        {item.test_id && (
          <div style={{ fontSize: "11px", color: "#6b7280", fontWeight: 500, marginTop: "4px" }}>{t("testId")}: {item.test_id}</div>
        )}
      </div>
    </div>
  );

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "24px", padding: "10px 0 40px" }}>
      {/* Section 1: Executive Summary */}
      {executive_summary && (
        <SectionCard title={t("executiveSummary")} icon={<i className="fas fa-chart-pie"></i>}>
          <div
            style={{
              background: priorityStyle.gradient,
              borderRadius: "12px",
              padding: "28px",
              color: "white",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              textAlign: "center",
              gap: "14px",
            }}
          >
            <div style={{ fontSize: "12px", textTransform: "uppercase", letterSpacing: "0.08em", opacity: 0.85 }}>
              {t("overallRisk")}
            </div>
            <div style={{ fontSize: "11px", fontWeight: 500, opacity: 0.8, marginTop: "-6px" }}>
              {t("basedOnGeneratedPrompts")}
            </div>
            <div style={{ fontSize: "36px", fontWeight: 700, opacity: 0.95, letterSpacing: "0.08em" }}>
              {overallRisk ? levelDisplayNames[overallRisk.toLowerCase()] || overallRisk : t("notAvailable")}
            </div>
            {executive_summary.security_posture && (
              <div style={{ fontSize: "14px", opacity: 0.9, lineHeight: 1.4 }}>
                {executive_summary.security_posture}
              </div>
            )}
            {executive_summary.final_verdict && (
              <div style={{ fontSize: "13px", opacity: 0.9, lineHeight: 1.5, marginTop: "4px" }}>
                {executive_summary.final_verdict}
              </div>
            )}
            {executive_summary.remediation_timeline && (
              <div style={{ fontSize: "13px", opacity: 0.9, lineHeight: 1.5, marginTop: "4px" }}>
                {executive_summary.remediation_timeline}
              </div>
            )}
          </div>
          {(summary ||
            summary?.critical_vulnerabilities != null ||
            summary?.high_vulnerabilities != null ||
            summary?.medium_vulnerabilities != null ||
            summary?.low_vulnerabilities != null ||
            executive_summary.readiness_score != null) && (
            <div style={{ display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gap: "20px" }}>
              <div style={{ display: "flex", flexWrap: "wrap", gap: "10px", alignItems: "center" }}>
                {executive_summary.readiness_score != null && (
                  <span
                    style={{
                      padding: "8px 14px",
                      borderRadius: "999px",
                      fontSize: "13px",
                      fontWeight: 600,
                      color: "#1e40af",
                      background: "#dbeafe",
                      border: "1px solid #93c5fd",
                    }}
                  >
                    {Number(executive_summary.readiness_score).toFixed(1)}% readiness
                  </span>
                )}
                {summary?.total_tests != null && (
                  <span
                    style={{
                      padding: "8px 14px",
                      borderRadius: "999px",
                      fontSize: "13px",
                      fontWeight: 600,
                      color: "#1e40af",
                      background: "#dbeafe",
                      border: "1px solid #93c5fd",
                    }}
                  >
                    {summary.total_tests} tests
                  </span>
                )}
                {summary?.total_passed != null && (
                  <span
                    style={{
                      padding: "8px 14px",
                      borderRadius: "999px",
                      fontSize: "13px",
                      fontWeight: 600,
                      color: "#fff",
                      background: "#16a34a",
                      border: "1px solid #15803d",
                    }}
                  >
                    {summary.total_passed} passed
                  </span>
                )}
                {summary?.total_failed != null && (
                  <span
                    style={{
                      padding: "8px 14px",
                      borderRadius: "999px",
                      fontSize: "13px",
                      fontWeight: 600,
                      color: "#fff",
                      background: "#dc2626",
                      border: "1px solid #b91c1c",
                    }}
                  >
                    {summary.total_failed} failed
                  </span>
                )}
                {summary?.pass_rate != null && (
                  <span
                    style={{
                      padding: "8px 14px",
                      borderRadius: "999px",
                      fontSize: "13px",
                      fontWeight: 600,
                      color: "#5b21b6",
                      background: "#ede9fe",
                      border: "1px solid #a78bfa",
                    }}
                  >
                    {Number(summary.pass_rate).toFixed(1)}% pass rate
                  </span>
                )}
              </div>
              <div style={{ display: "flex", flexWrap: "wrap", gap: "10px", alignItems: "center" }}>
                {summary?.vulnerable_categories != null && (
                  <span
                    style={{
                      padding: "8px 14px",
                      borderRadius: "999px",
                      fontSize: "13px",
                      fontWeight: 600,
                      color: "#fff",
                      background: "#dc2626",
                      border: "1px solid #b91c1c",
                    }}
                  >
                    {summary.vulnerable_categories} {t("vulnerableCategoriesChip")}
                  </span>
                )}
                {summary?.critical_vulnerabilities != null && (
                  <span
                    style={{
                      padding: "8px 14px",
                      borderRadius: "999px",
                      fontSize: "13px",
                      fontWeight: 600,
                      color: "#fff",
                      background: "#b91c1c",
                      border: "1px solid #991b1b",
                    }}
                  >
                    {summary.critical_vulnerabilities} {t("criticalChip")}
                  </span>
                )}
                {summary?.high_vulnerabilities != null && (
                  <span
                    style={{
                      padding: "8px 14px",
                      borderRadius: "999px",
                      fontSize: "13px",
                      fontWeight: 600,
                      color: "#fff",
                      background: "#ea580c",
                      border: "1px solid #c2410c",
                    }}
                  >
                    {summary.high_vulnerabilities} {t("highChip")}
                  </span>
                )}
                {summary?.medium_vulnerabilities != null && (
                  <span
                    style={{
                      padding: "8px 14px",
                      borderRadius: "999px",
                      fontSize: "13px",
                      fontWeight: 600,
                      color: "#fff",
                      background: "#d97706",
                      border: "1px solid #b45309",
                    }}
                  >
                    {summary.medium_vulnerabilities} {t("mediumChip")}
                  </span>
                )}
                {summary?.low_vulnerabilities != null && (
                  <span
                    style={{
                      padding: "8px 14px",
                      borderRadius: "999px",
                      fontSize: "13px",
                      fontWeight: 600,
                      color: "#fff",
                      background: "#16a34a",
                      border: "1px solid #15803d",
                    }}
                  >
                    {summary.low_vulnerabilities} {t("lowChip")}
                  </span>
                )}
              </div>
            </div>
          )}
          {(executive_summary.key_findings?.length > 0 || executive_summary.business_impact?.length > 0) && (
            <div style={{ display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gap: "20px" }}>
              {executive_summary.key_findings?.length > 0 && (
                <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
                  <div
                    style={{
                      fontSize: "12px",
                      textTransform: "uppercase",
                      letterSpacing: "0.08em",
                      color: "#3b82f6",
                      fontWeight: 600,
                    }}
                  >
                    {t("keyFindings")}
                  </div>
                  {executive_summary.key_findings.map((item, i) => (
                    <div
                      key={i}
                      style={{
                        padding: "10px 14px",
                        background: "rgba(59, 130, 246, 0.06)",
                        borderRadius: "8px",
                        fontSize: "14px",
                        color: "var(--card-text)",
                        lineHeight: 1.5,
                      }}
                    >
                      {item}
                    </div>
                  ))}
                </div>
              )}
              {executive_summary.business_impact?.length > 0 && (
                <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
                  <div
                    style={{
                      fontSize: "12px",
                      textTransform: "uppercase",
                      letterSpacing: "0.08em",
                      color: "#ef4444",
                      fontWeight: 600,
                    }}
                  >
                    {t("businessImpact")}
                  </div>
                  {executive_summary.business_impact.map((item, i) => (
                    <div
                      key={i}
                      style={{
                        padding: "10px 14px",
                        background: "rgba(239, 68, 68, 0.06)",
                        borderRadius: "8px",
                        fontSize: "14px",
                        color: "var(--card-text)",
                        lineHeight: 1.5,
                      }}
                    >
                      {item}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </SectionCard>
      )}

      {/* Section 2: Security Assessment */}
      {security_assessment && (
        <SectionCardAccordion title={t("securityAssessment")} icon={<i className="fas fa-shield-alt"></i>}>
          <div style={{ display: "flex", flexDirection: "column", gap: "20px" }}>
            {security_assessment.vulnerable_categories?.length > 0 && (
              <div
                style={{
                  background: "linear-gradient(135deg, rgba(239, 68, 68, 0.06), rgba(185, 28, 28, 0.04))",
                  borderRadius: "12px",
                  padding: "20px",
                  border: "1px solid rgba(239, 68, 68, 0.2)",
                }}
              >
                <div
                  style={{
                    fontSize: "11px",
                    textTransform: "uppercase",
                    letterSpacing: "0.1em",
                    color: "#dc2626",
                    marginBottom: "14px",
                    fontWeight: 700,
                  }}
                >
                  {t("vulnerableCategories")}
                </div>
                <div style={{ display: "flex", flexWrap: "wrap", gap: "12px" }}>
                  {security_assessment.vulnerable_categories.map((v, i) => {
                    const failureRate = Number(v.failure_rate);
                    const isCritical = failureRate >= 80;
                    const isHigh = failureRate >= 50 && failureRate < 80;
                    const bg = isCritical ? "#7f1d1d" : isHigh ? "#991b1b" : "#9a3412";
                    const border = isCritical ? "#450a0a" : isHigh ? "#7f1d1d" : "#7c2d12";
                    return (
                      <span
                        key={i}
                        style={{
                          padding: "10px 16px",
                          borderRadius: "10px",
                          fontSize: "13px",
                          fontWeight: 600,
                          color: "#fecaca",
                          background: bg,
                          border: `1px solid ${border}`,
                          boxShadow: "0 2px 6px rgba(0, 0, 0, 0.15)",
                        }}
                      >
                        {getAttackTypeLabel(v.category)}
                        <span style={{ opacity: 0.95, fontWeight: 500, marginLeft: "6px" }}>
                          · {failureRate.toFixed(1)}% failure
                        </span>
                      </span>
                    );
                  })}
                </div>
              </div>
            )}

            {security_assessment.attack_success_rates &&
              Object.keys(security_assessment.attack_success_rates).length > 0 && (
                <div>
                  <div
                    style={{
                      fontSize: "12px",
                      textTransform: "uppercase",
                      letterSpacing: "0.08em",
                      color: "var(--muted-text)",
                      marginBottom: "12px",
                      fontWeight: 600,
                    }}
                  >
                    {t("attackSuccessRates")}
                  </div>
                  <div style={{ overflowX: "auto" }}>
                    <table
                      style={{
                        width: "100%",
                        borderCollapse: "separate",
                        borderSpacing: "0",
                        fontSize: "14px",
                      }}
                    >
                      <thead>
                        <tr>
                          <th style={{ ...tableHeaderStyle, borderTopLeftRadius: "10px" }}>{t("attackType")}</th>
                          <th style={tableHeaderStyle}>{t("risk")}</th>
                          <th style={tableHeaderStyle}>{t("successRate")}</th>
                          <th style={tableHeaderStyle}>{t("successful")}</th>
                          <th style={tableHeaderStyle}>{t("totalRequests")}</th>
                          <th style={{ ...tableHeaderStyle, borderTopRightRadius: "10px" }}>{t("failureRate")}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {Object.entries(security_assessment.attack_success_rates).map(([attack, m], i) => {
                          const isLast =
                            i === Object.keys(security_assessment.attack_success_rates).length - 1;
                          const riskKey = m.risk_level?.toLowerCase();
                          const isKnownRisk = riskKey && levelDisplayNames[riskKey];
                          const riskStyle = isKnownRisk ? getRiskLevelPriorityColors(riskKey) : null;
                          const baseTd = {
                            padding: "12px 16px",
                            borderBottom: isLast ? "none" : "1px solid var(--card-border)",
                            verticalAlign: "middle",
                          };
                          return (
                            <tr
                              key={attack}
                              style={{
                                background: i % 2 === 0 ? "transparent" : "rgba(59, 130, 246, 0.03)",
                              }}
                            >
                              <td
                                style={{
                                  ...baseTd,
                                  fontWeight: 600,
                                  color: "var(--card-text)",
                                  ...(isLast ? { borderBottomLeftRadius: "10px" } : {}),
                                }}
                              >
                                {getAttackTypeLabel(attack)}
                              </td>
                              <td style={baseTd}>
                                <span
                                  style={{
                                    padding: "4px 10px",
                                    borderRadius: "999px",
                                    fontSize: "11px",
                                    fontWeight: 600,
                                    color: "white",
                                    background: riskStyle ? riskStyle.gradient : "#6b7280",
                                  }}
                                >
                                  {riskStyle ? levelDisplayNames[riskKey] || m.risk_level : t("unknown")}
                                </span>
                              </td>
                              <td style={baseTd}>
                                {m.success_rate != null ? `${(m.success_rate * 100).toFixed(1)}%` : t("notAvailable")}
                              </td>
                              <td style={baseTd}>{m.successful_count ?? t("notAvailable")}</td>
                              <td style={baseTd}>{m.total_requests ?? t("notAvailable")}</td>
                              <td
                                style={{
                                  ...baseTd,
                                  ...(isLast ? { borderBottomRightRadius: "10px" } : {}),
                                }}
                              >
                                {m.failure_rate != null ? `${(m.failure_rate * 100).toFixed(1)}%` : t("notAvailable")}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

            {security_assessment.subcategory_stats &&
              Object.keys(security_assessment.subcategory_stats).length > 0 && (
                <div>
                  <div
                    style={{
                      fontSize: "12px",
                      textTransform: "uppercase",
                      letterSpacing: "0.08em",
                      color: "var(--muted-text)",
                      marginBottom: "12px",
                      fontWeight: 600,
                    }}
                  >
                    {t("categoryMetrics")}
                  </div>
                  <div style={{ overflowX: "auto" }}>
                    <table
                      style={{
                        width: "100%",
                        borderCollapse: "separate",
                        borderSpacing: "0",
                        fontSize: "13px",
                      }}
                    >
                      <thead>
                        <tr>
                          <th style={{ ...tableHeaderStyle, borderTopLeftRadius: "10px" }}>{t("category")}</th>
                          <th style={tableHeaderStyle}>{t("tests")}</th>
                          <th style={tableHeaderStyle}>{t("passed")}</th>
                          <th style={tableHeaderStyle}>{t("failed")}</th>
                          <th style={tableHeaderStyle}>{t("readiness")}</th>
                          <th style={{ ...tableHeaderStyle, borderTopRightRadius: "10px" }}>
                            {t("failureRate")}
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {Object.entries(security_assessment.subcategory_stats).map(([cat, m], i) => {
                          const isLast =
                            i === Object.keys(security_assessment.subcategory_stats).length - 1;
                          const baseTd = {
                            padding: "12px 16px",
                            borderBottom: isLast ? "none" : "1px solid var(--card-border)",
                            verticalAlign: "middle",
                          };
                          return (
                            <tr
                              key={cat}
                              style={{
                                background: i % 2 === 0 ? "transparent" : "rgba(59, 130, 246, 0.03)",
                              }}
                            >
                              <td
                                style={{
                                  ...baseTd,
                                  fontWeight: 600,
                                  color: "var(--card-text)",
                                  ...(isLast ? { borderBottomLeftRadius: "10px" } : {}),
                                }}
                              >
                                {getAttackTypeLabel(cat)}
                              </td>
                              <td style={baseTd}>{m.total_tests ?? t("notAvailable")}</td>
                              <td style={baseTd}>{m.passed ?? t("notAvailable")}</td>
                              <td style={baseTd}>{m.failed ?? t("notAvailable")}</td>
                              <td style={baseTd}>
                                {m.readiness != null ? `${Number(m.readiness).toFixed(1)}%` : t("notAvailable")}
                              </td>
                              <td
                                style={{
                                  ...baseTd,
                                  ...(isLast ? { borderBottomRightRadius: "10px" } : {}),
                                }}
                              >
                                {m.failure_rate != null ? `${Number(m.failure_rate).toFixed(1)}%` : t("notAvailable")}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
          </div>
        </SectionCardAccordion>
      )}

      {/* Section 3: Recommendations */}
      {(critical_recommendations?.length ||
        high_priority_recommendations?.length ||
        medium_priority_recommendations?.length) && (
        <SectionCardAccordion title={t("recommendations")} icon={<i className="fas fa-lightbulb"></i>}>
          <div style={{ display: "flex", flexDirection: "column", gap: "24px" }}>
            {critical_recommendations?.length > 0 && (
              <div
                style={{
                  background: "rgba(239, 68, 68, 0.06)",
                  borderRadius: "12px",
                  padding: "16px",
                  border: "1px solid rgba(239, 68, 68, 0.15)",
                }}
              >
                <div style={{ fontSize: "12px", textTransform: "uppercase", letterSpacing: "0.08em", color: "#ef4444", marginBottom: "12px", fontWeight: 700 }}>
                  {t("criticalPriority")}
                </div>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "16px" }}>
                  {critical_recommendations.map((item, i) => renderRecommendationCard(item, i))}
                </div>
              </div>
            )}

            {high_priority_recommendations?.length > 0 && (
              <div
                style={{
                  background: "rgba(245, 158, 11, 0.06)",
                  borderRadius: "12px",
                  padding: "16px",
                  border: "1px solid rgba(245, 158, 11, 0.2)",
                }}
              >
                <div style={{ fontSize: "12px", textTransform: "uppercase", letterSpacing: "0.08em", color: "#d97706", marginBottom: "12px", fontWeight: 700 }}>
                  {t("highPriority")}
                </div>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "16px" }}>
                  {high_priority_recommendations.map((item, i) => renderRecommendationCard(item, i))}
                </div>
              </div>
            )}

            {medium_priority_recommendations?.length > 0 && (
              <div
                style={{
                  background: "rgba(59, 130, 246, 0.06)",
                  borderRadius: "12px",
                  padding: "16px",
                  border: "1px solid rgba(59, 130, 246, 0.2)",
                }}
              >
                <div style={{ fontSize: "12px", textTransform: "uppercase", letterSpacing: "0.08em", color: "#2563eb", marginBottom: "12px", fontWeight: 700 }}>
                  {t("mediumPriority")}
                </div>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "16px" }}>
                  {medium_priority_recommendations.map((item, i) => renderRecommendationCard(item, i))}
                </div>
              </div>
            )}
          </div>
        </SectionCardAccordion>
      )}

      {/* Section 4: Monitoring */}
      {monitoring_recommendations?.length > 0 && (
        <SectionCardAccordion title={t("monitoringRecommendations")} icon={<i className="fas fa-chart-line"></i>}>
          <div style={{ display: "flex", flexDirection: "column", gap: "20px" }}>
            {monitoring_recommendations.map((mon, idx) => (
              <div
                key={idx}
                style={{
                  border: "1px solid var(--card-border)",
                  borderRadius: "12px",
                  overflow: "hidden",
                  background: "var(--card-bg)",
                }}
              >
                <div
                  style={{
                    padding: "12px 16px",
                    borderBottom: "1px solid var(--card-border)",
                    display: "flex",
                    flexDirection: "column",
                    gap: "8px",
                  }}
                >
                  <div style={{ display: "flex", alignItems: "center", gap: "10px", flexWrap: "wrap" }}>
                    <div style={{ fontSize: "14px", fontWeight: 600, color: "var(--card-text)" }}>
                      {mon.title || t("securityMonitoring")}
                      {mon.category && (
                        <span style={{ fontWeight: 500, opacity: 0.85 }}> ({mon.category})</span>
                      )}
                    </div>
                    {mon.priority && (
                      <span
                        style={{
                          padding: "4px 10px",
                          borderRadius: "999px",
                          fontSize: "11px",
                          fontWeight: 600,
                          color: "white",
                          background: getRiskLevelPriorityColors(mon.priority?.toLowerCase()).gradient,
                        }}
                      >
                        {levelDisplayNames[mon.priority?.toLowerCase()] || mon.priority}
                      </span>
                    )}
                  </div>
                  {mon.description && (
                    <div style={{ fontSize: "13px", color: "var(--card-text)", lineHeight: 1.5, opacity: 0.9 }}>
                      {mon.description}
                    </div>
                  )}
                </div>
                <div style={{ padding: "16px", display: "flex", flexDirection: "column", gap: "16px" }}>
                    {mon.metrics_to_monitor?.length > 0 && (
                      <div style={{ width: "100%", minWidth: 0 }}>
                        <div
                          style={{
                            fontSize: "12px",
                            textTransform: "uppercase",
                            letterSpacing: "0.08em",
                            color: "#3b82f6",
                            marginBottom: "8px",
                            fontWeight: 600,
                          }}
                        >
                          {t("metrics")}
                        </div>
                        <div style={{ overflowX: "auto", width: "100%" }}>
                          <table
                            style={{
                              width: "100%",
                              minWidth: "100%",
                              borderCollapse: "separate",
                              borderSpacing: "0",
                              fontSize: "13px",
                            }}
                          >
                            <thead>
                              <tr>
                                <th style={tableHeaderStyle}>{t("metric")}</th>
                                <th style={tableHeaderStyle}>{t("threshold")}</th>
                                <th style={tableHeaderStyle}>{t("action")}</th>
                                <th style={tableHeaderStyle}>{t("currentIssue")}</th>
                              </tr>
                            </thead>
                            <tbody>
                              {mon.metrics_to_monitor.map((row, i) => (
                                <tr
                                  key={i}
                                  style={{
                                    background:
                                      i % 2 === 0 ? "transparent" : "rgba(59, 130, 246, 0.03)",
                                  }}
                                >
                                  <td
                                    style={{
                                      padding: "10px 12px",
                                      borderBottom: "1px solid var(--card-border)",
                                      color: "var(--card-text)",
                                    }}
                                  >
                                    {row.metric}
                                  </td>
                                  <td
                                    style={{
                                      padding: "10px 12px",
                                      borderBottom: "1px solid var(--card-border)",
                                      color: "var(--card-text)",
                                    }}
                                  >
                                    {row.threshold}
                                  </td>
                                  <td
                                    style={{
                                      padding: "10px 12px",
                                      borderBottom: "1px solid var(--card-border)",
                                      color: "var(--card-text)",
                                    }}
                                  >
                                    {row.action}
                                  </td>
                                  <td
                                    style={{
                                      padding: "10px 12px",
                                      borderBottom: "1px solid var(--card-border)",
                                      color: "var(--card-text)",
                                    }}
                                  >
                                    {row.current_issue || t("notAvailable")}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    )}
                    {(mon.logging_requirements?.length > 0 || mon.alerting_rules?.length > 0 || mon.tools_recommended?.length > 0) && (
                      <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: "20px", width: "100%" }}>
                        {mon.logging_requirements?.length > 0 && (
                          <div>
                            <div
                              style={{
                                fontSize: "12px",
                                textTransform: "uppercase",
                                letterSpacing: "0.08em",
                                color: "#22c55e",
                                marginBottom: "6px",
                                fontWeight: 600,
                              }}
                            >
                              {t("loggingRequirements")}
                            </div>
                            <ul
                              style={{
                                margin: 0,
                                paddingLeft: "18px",
                                fontSize: "14px",
                                color: "var(--card-text)",
                                lineHeight: 1.6,
                              }}
                            >
                              {mon.logging_requirements.map((t, i) => (
                                <li key={i}>{t}</li>
                              ))}
                            </ul>
                          </div>
                        )}
                        {mon.alerting_rules?.length > 0 && (
                          <div>
                            <div
                              style={{
                                fontSize: "12px",
                                textTransform: "uppercase",
                                letterSpacing: "0.08em",
                                color: "#ef4444",
                                marginBottom: "6px",
                                fontWeight: 600,
                              }}
                            >
                              {t("alertingRules")}
                            </div>
                            <ul
                              style={{
                                margin: 0,
                                paddingLeft: "18px",
                                fontSize: "14px",
                                color: "var(--card-text)",
                                lineHeight: 1.6,
                              }}
                            >
                              {mon.alerting_rules.map((t, i) => (
                                <li key={i}>{t}</li>
                              ))}
                            </ul>
                          </div>
                        )}
                        {mon.tools_recommended?.length > 0 && (
                          <div>
                            <div
                              style={{
                                fontSize: "12px",
                                textTransform: "uppercase",
                                letterSpacing: "0.08em",
                                color: "#8b5cf6",
                                marginBottom: "6px",
                                fontWeight: 600,
                              }}
                            >
                              {t("tools")}
                            </div>
                            <ul
                              style={{
                                margin: 0,
                                paddingLeft: "18px",
                                fontSize: "14px",
                                color: "var(--card-text)",
                                lineHeight: 1.6,
                              }}
                            >
                              {mon.tools_recommended.map((t, i) => (
                                <li key={i}>{t}</li>
                              ))}
                            </ul>
                          </div>
                        )}
                      </div>
                    )}
                </div>
              </div>
            ))}
          </div>
        </SectionCardAccordion>
      )}

      {/* Section 5: Compliance & Governance */}
      {compliance_recommendations?.length > 0 && (
        <SectionCardAccordion title={t("complianceGovernance")} icon={<i className="fas fa-balance-scale"></i>}>
          <div style={{ display: "flex", flexDirection: "column", gap: "20px" }}>
            {compliance_recommendations.map((item, idx) => (
              <div
                key={idx}
                style={{
                  border: "1px solid var(--card-border)",
                  borderRadius: "12px",
                  overflow: "hidden",
                  background: "var(--card-bg)",
                }}
              >
                <div
                  style={{
                    padding: "12px 16px",
                    borderBottom: "1px solid var(--card-border)",
                    display: "flex",
                    flexDirection: "column",
                    gap: "8px",
                  }}
                >
                  <div style={{ display: "flex", alignItems: "center", gap: "10px", flexWrap: "wrap" }}>
                    <div style={{ fontSize: "14px", fontWeight: 600, color: "var(--card-text)" }}>
                      {item.title || t("compliance")}
                      {item.category && (
                        <span style={{ fontWeight: 500, opacity: 0.85 }}> ({item.category})</span>
                      )}
                    </div>
                    {item.priority && (
                      <span
                        style={{
                          padding: "4px 10px",
                          borderRadius: "999px",
                          fontSize: "11px",
                          fontWeight: 600,
                          color: "white",
                          background: getRiskLevelPriorityColors(item.priority?.toLowerCase()).gradient,
                        }}
                      >
                        {levelDisplayNames[item.priority?.toLowerCase()] || item.priority} {t("priority")}
                      </span>
                    )}
                  </div>
                  {item.description && (
                    <div style={{ fontSize: "13px", color: "var(--card-text)", lineHeight: 1.5, opacity: 0.9 }}>
                      {item.description}
                    </div>
                  )}
                </div>
                <div style={{ padding: "16px", display: "flex", flexDirection: "column", gap: "16px" }}>
                  {(item.policies_to_create?.length > 0 || item.security_reviews?.length > 0 || item.compliance_requirements?.length > 0 || item.documentation_required?.length > 0) && (
                    <div style={{ display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gap: "20px", width: "100%" }}>
                      {item.policies_to_create?.length > 0 && (
                        <div>
                          <div
                            style={{
                              fontSize: "12px",
                              textTransform: "uppercase",
                              letterSpacing: "0.08em",
                              color: "#3b82f6",
                              marginBottom: "6px",
                              fontWeight: 600,
                            }}
                          >
                            {t("policiesToCreate")}
                          </div>
                          <ul
                            style={{
                              margin: 0,
                              paddingLeft: "18px",
                              fontSize: "14px",
                              color: "var(--card-text)",
                              lineHeight: 1.6,
                            }}
                          >
                            {item.policies_to_create.map((t, i) => (
                              <li key={i}>{t}</li>
                            ))}
                          </ul>
                        </div>
                      )}
                      {item.security_reviews?.length > 0 && (
                        <div>
                          <div
                            style={{
                              fontSize: "12px",
                              textTransform: "uppercase",
                              letterSpacing: "0.08em",
                              color: "#22c55e",
                              marginBottom: "6px",
                              fontWeight: 600,
                            }}
                          >
                            {t("securityReviews")}
                          </div>
                          <ul
                            style={{
                              margin: 0,
                              paddingLeft: "18px",
                              fontSize: "14px",
                              color: "var(--card-text)",
                              lineHeight: 1.6,
                            }}
                          >
                            {item.security_reviews.map((t, i) => (
                              <li key={i}>{t}</li>
                            ))}
                          </ul>
                        </div>
                      )}
                      {item.compliance_requirements?.length > 0 && (
                        <div>
                          <div
                            style={{
                              fontSize: "12px",
                              textTransform: "uppercase",
                              letterSpacing: "0.08em",
                              color: "#8b5cf6",
                              marginBottom: "6px",
                              fontWeight: 600,
                            }}
                          >
                            {t("complianceRequirements")}
                          </div>
                          <ul
                            style={{
                              margin: 0,
                              paddingLeft: "18px",
                              fontSize: "14px",
                              color: "var(--card-text)",
                              lineHeight: 1.6,
                            }}
                          >
                            {item.compliance_requirements.map((t, i) => (
                              <li key={i}>{t}</li>
                            ))}
                          </ul>
                        </div>
                      )}
                      {item.documentation_required?.length > 0 && (
                        <div>
                          <div
                            style={{
                              fontSize: "12px",
                              textTransform: "uppercase",
                              letterSpacing: "0.08em",
                              color: "var(--muted-text)",
                              marginBottom: "6px",
                              fontWeight: 600,
                            }}
                          >
                            {t("documentationRequired")}
                          </div>
                          <ul
                            style={{
                              margin: 0,
                              paddingLeft: "18px",
                              fontSize: "14px",
                              color: "var(--card-text)",
                              lineHeight: 1.6,
                            }}
                          >
                            {item.documentation_required.map((t, i) => (
                              <li key={i}>{t}</li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </SectionCardAccordion>
      )}

      {/* Section 6: Implementation Roadmap */}
      {implementation_roadmap && (
        <SectionCardAccordion title={t("implementationRoadmap")} icon={<i className="fas fa-road"></i>}>
          <div style={{ display: "flex", flexDirection: "column", gap: "24px" }}>
            {implementation_roadmap.phase_1_immediate_24_48h?.length > 0 && (
              <div>
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "12px",
                    marginBottom: "12px",
                  }}
                >
                  <div
                    style={{
                      width: "32px",
                      height: "32px",
                      borderRadius: "8px",
                      background: "linear-gradient(135deg, #ef4444, #dc2626)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <span style={{ fontSize: "14px", color: "white", fontWeight: 700 }}>1</span>
                  </div>
                  <div style={{ fontSize: "16px", fontWeight: 600, color: "var(--card-text)" }}>
                    {t("phase1Immediate")}
                  </div>
                </div>
                <div style={{ overflowX: "auto" }}>
                  <table
                    style={{
                      width: "100%",
                      borderCollapse: "separate",
                      borderSpacing: "0",
                      fontSize: "14px",
                      tableLayout: "fixed",
                    }}
                  >
                    <thead>
                      <tr>
                        <th
                          style={{
                            ...tableHeaderStyle,
                            width: "40%",
                            borderTopLeftRadius: "10px",
                            background: "rgba(239, 68, 68, 0.08)",
                            borderBottom: "1px solid rgba(239, 68, 68, 0.2)",
                          }}
                        >
                          {t("task")}
                        </th>
                        <th
                          style={{
                            ...tableHeaderStyle,
                            width: "20%",
                            textAlign: "center",
                            background: "rgba(239, 68, 68, 0.08)",
                            borderBottom: "1px solid rgba(239, 68, 68, 0.2)",
                          }}
                        >
                          {t("owner")}
                        </th>
                        <th
                          style={{
                            ...tableHeaderStyle,
                            width: "20%",
                            textAlign: "center",
                            background: "rgba(239, 68, 68, 0.08)",
                            borderBottom: "1px solid rgba(239, 68, 68, 0.2)",
                          }}
                        >
                          {t("priority")}
                        </th>
                        <th
                          style={{
                            ...tableHeaderStyle,
                            width: "20%",
                            textAlign: "center",
                            borderTopRightRadius: "10px",
                            background: "rgba(239, 68, 68, 0.08)",
                            borderBottom: "1px solid rgba(239, 68, 68, 0.2)",
                          }}
                        >
                          {t("timeline")}
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {implementation_roadmap.phase_1_immediate_24_48h.map((item, index) => {
                        const isLast =
                          index === implementation_roadmap.phase_1_immediate_24_48h.length - 1;
                        return (
                          <tr
                            key={index}
                            style={{
                              background:
                                index % 2 === 0 ? "transparent" : "rgba(239, 68, 68, 0.03)",
                            }}
                          >
                            <td
                              style={{
                                padding: "14px 16px",
                                fontWeight: 600,
                                color: "var(--card-text)",
                                borderBottom: isLast
                                  ? "none"
                                  : "1px solid rgba(239, 68, 68, 0.1)",
                                borderBottomLeftRadius: isLast ? "10px" : "0",
                                verticalAlign: "middle",
                              }}
                            >
                              {item.task}
                            </td>
                            <td
                              style={{
                                padding: "14px 16px",
                                textAlign: "center",
                                color: "var(--muted-text)",
                                borderBottom: isLast
                                  ? "none"
                                  : "1px solid rgba(239, 68, 68, 0.1)",
                                verticalAlign: "middle",
                              }}
                            >
                              {item.owner}
                            </td>
                            <td
                              style={{
                                padding: "14px 16px",
                                textAlign: "center",
                                borderBottom: isLast
                                  ? "none"
                                  : "1px solid rgba(239, 68, 68, 0.1)",
                                verticalAlign: "middle",
                              }}
                            >
                              <span
                                style={{
                                  padding: "5px 12px",
                                  borderRadius: "999px",
                                  fontSize: "11px",
                                  fontWeight: 600,
                                  color: "white",
                                  background: getRiskLevelPriorityColors(
                                    item.priority?.toLowerCase() || "medium"
                                  ).gradient,
                                  whiteSpace: "nowrap",
                                }}
                              >
                                {levelDisplayNames[item.priority?.toLowerCase()] || item.priority}
                              </span>
                            </td>
                            <td
                              style={{
                                padding: "14px 16px",
                                textAlign: "center",
                                borderBottom: isLast
                                  ? "none"
                                  : "1px solid rgba(239, 68, 68, 0.1)",
                                borderBottomRightRadius: isLast ? "10px" : "0",
                                verticalAlign: "middle",
                              }}
                            >
                              <span
                                style={{
                                  padding: "4px 10px",
                                  borderRadius: "6px",
                                  fontSize: "12px",
                                  fontWeight: 500,
                                  color: "#3b82f6",
                                  background: "rgba(59, 130, 246, 0.1)",
                                  whiteSpace: "nowrap",
                                }}
                              >
                                {item.estimated_effort}
                              </span>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {implementation_roadmap.phase_2_short_term_1_week?.length > 0 && (
              <div>
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "12px",
                    marginBottom: "12px",
                  }}
                >
                  <div
                    style={{
                      width: "32px",
                      height: "32px",
                      borderRadius: "8px",
                      background: "linear-gradient(135deg, #f59e0b, #d97706)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <span style={{ fontSize: "14px", color: "white", fontWeight: 700 }}>2</span>
                  </div>
                  <div style={{ fontSize: "16px", fontWeight: 600, color: "var(--card-text)" }}>
                    {t("phase2ShortTerm")}
                  </div>
                </div>
                <div style={{ overflowX: "auto" }}>
                  <table
                    style={{
                      width: "100%",
                      borderCollapse: "separate",
                      borderSpacing: "0",
                      fontSize: "14px",
                      tableLayout: "fixed",
                    }}
                  >
                    <thead>
                      <tr>
                        <th
                          style={{
                            ...tableHeaderStyle,
                            width: "40%",
                            borderTopLeftRadius: "10px",
                            background: "rgba(245, 158, 11, 0.08)",
                            borderBottom: "1px solid rgba(245, 158, 11, 0.2)",
                          }}
                        >
                          {t("task")}
                        </th>
                        <th
                          style={{
                            ...tableHeaderStyle,
                            width: "20%",
                            textAlign: "center",
                            background: "rgba(245, 158, 11, 0.08)",
                            borderBottom: "1px solid rgba(245, 158, 11, 0.2)",
                          }}
                        >
                          {t("owner")}
                        </th>
                        <th
                          style={{
                            ...tableHeaderStyle,
                            width: "20%",
                            textAlign: "center",
                            background: "rgba(245, 158, 11, 0.08)",
                            borderBottom: "1px solid rgba(245, 158, 11, 0.2)",
                          }}
                        >
                          {t("priority")}
                        </th>
                        <th
                          style={{
                            ...tableHeaderStyle,
                            width: "20%",
                            textAlign: "center",
                            borderTopRightRadius: "10px",
                            background: "rgba(245, 158, 11, 0.08)",
                            borderBottom: "1px solid rgba(245, 158, 11, 0.2)",
                          }}
                        >
                          {t("timeline")}
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {implementation_roadmap.phase_2_short_term_1_week.map((item, index) => {
                        const isLast =
                          index === implementation_roadmap.phase_2_short_term_1_week.length - 1;
                        return (
                          <tr
                            key={index}
                            style={{
                              background:
                                index % 2 === 0 ? "transparent" : "rgba(245, 158, 11, 0.03)",
                            }}
                          >
                            <td
                              style={{
                                padding: "14px 16px",
                                fontWeight: 600,
                                color: "var(--card-text)",
                                borderBottom: isLast
                                  ? "none"
                                  : "1px solid rgba(245, 158, 11, 0.1)",
                                borderBottomLeftRadius: isLast ? "10px" : "0",
                                verticalAlign: "middle",
                              }}
                            >
                              {item.task}
                            </td>
                            <td
                              style={{
                                padding: "14px 16px",
                                textAlign: "center",
                                color: "var(--muted-text)",
                                borderBottom: isLast
                                  ? "none"
                                  : "1px solid rgba(245, 158, 11, 0.1)",
                                verticalAlign: "middle",
                              }}
                            >
                              {item.owner}
                            </td>
                            <td
                              style={{
                                padding: "14px 16px",
                                textAlign: "center",
                                borderBottom: isLast
                                  ? "none"
                                  : "1px solid rgba(245, 158, 11, 0.1)",
                                verticalAlign: "middle",
                              }}
                            >
                              <span
                                style={{
                                  padding: "5px 12px",
                                  borderRadius: "999px",
                                  fontSize: "11px",
                                  fontWeight: 600,
                                  color: "white",
                                  background: getRiskLevelPriorityColors(
                                    item.priority?.toLowerCase() || "medium"
                                  ).gradient,
                                  whiteSpace: "nowrap",
                                }}
                              >
                                {levelDisplayNames[item.priority?.toLowerCase()] || item.priority}
                              </span>
                            </td>
                            <td
                              style={{
                                padding: "14px 16px",
                                textAlign: "center",
                                borderBottom: isLast
                                  ? "none"
                                  : "1px solid rgba(245, 158, 11, 0.1)",
                                borderBottomRightRadius: isLast ? "10px" : "0",
                                verticalAlign: "middle",
                              }}
                            >
                              <span
                                style={{
                                  padding: "4px 10px",
                                  borderRadius: "6px",
                                  fontSize: "12px",
                                  fontWeight: 500,
                                  color: "#3b82f6",
                                  background: "rgba(59, 130, 246, 0.1)",
                                  whiteSpace: "nowrap",
                                }}
                              >
                                {item.estimated_effort}
                              </span>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {implementation_roadmap.phase_3_medium_term_2_4_weeks?.length > 0 && (
              <div>
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "12px",
                    marginBottom: "12px",
                  }}
                >
                  <div
                    style={{
                      width: "32px",
                      height: "32px",
                      borderRadius: "8px",
                      background: "linear-gradient(135deg, #22c55e, #16a34a)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <span style={{ fontSize: "14px", color: "white", fontWeight: 700 }}>3</span>
                  </div>
                  <div style={{ fontSize: "16px", fontWeight: 600, color: "var(--card-text)" }}>
                    {t("phase3MediumTerm")}
                  </div>
                </div>
                <div style={{ overflowX: "auto" }}>
                  <table
                    style={{
                      width: "100%",
                      borderCollapse: "separate",
                      borderSpacing: "0",
                      fontSize: "14px",
                      tableLayout: "fixed",
                    }}
                  >
                    <thead>
                      <tr>
                        <th
                          style={{
                            ...tableHeaderStyle,
                            width: "40%",
                            borderTopLeftRadius: "10px",
                            background: "rgba(34, 197, 94, 0.08)",
                            borderBottom: "1px solid rgba(34, 197, 94, 0.2)",
                          }}
                        >
                          {t("task")}
                        </th>
                        <th
                          style={{
                            ...tableHeaderStyle,
                            width: "20%",
                            textAlign: "center",
                            background: "rgba(34, 197, 94, 0.08)",
                            borderBottom: "1px solid rgba(34, 197, 94, 0.2)",
                          }}
                        >
                          {t("owner")}
                        </th>
                        <th
                          style={{
                            ...tableHeaderStyle,
                            width: "20%",
                            textAlign: "center",
                            background: "rgba(34, 197, 94, 0.08)",
                            borderBottom: "1px solid rgba(34, 197, 94, 0.2)",
                          }}
                        >
                          {t("priority")}
                        </th>
                        <th
                          style={{
                            ...tableHeaderStyle,
                            width: "20%",
                            textAlign: "center",
                            borderTopRightRadius: "10px",
                            background: "rgba(34, 197, 94, 0.08)",
                            borderBottom: "1px solid rgba(34, 197, 94, 0.2)",
                          }}
                        >
                          {t("timeline")}
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {implementation_roadmap.phase_3_medium_term_2_4_weeks.map((item, index) => {
                        const isLast =
                          index === implementation_roadmap.phase_3_medium_term_2_4_weeks.length - 1;
                        return (
                          <tr
                            key={index}
                            style={{
                              background:
                                index % 2 === 0 ? "transparent" : "rgba(34, 197, 94, 0.03)",
                            }}
                          >
                            <td
                              style={{
                                padding: "14px 16px",
                                fontWeight: 600,
                                color: "var(--card-text)",
                                borderBottom: isLast
                                  ? "none"
                                  : "1px solid rgba(34, 197, 94, 0.1)",
                                borderBottomLeftRadius: isLast ? "10px" : "0",
                                verticalAlign: "middle",
                              }}
                            >
                              {item.task}
                            </td>
                            <td
                              style={{
                                padding: "14px 16px",
                                textAlign: "center",
                                color: "var(--muted-text)",
                                borderBottom: isLast
                                  ? "none"
                                  : "1px solid rgba(34, 197, 94, 0.1)",
                                verticalAlign: "middle",
                              }}
                            >
                              {item.owner}
                            </td>
                            <td
                              style={{
                                padding: "14px 16px",
                                textAlign: "center",
                                borderBottom: isLast
                                  ? "none"
                                  : "1px solid rgba(34, 197, 94, 0.1)",
                                verticalAlign: "middle",
                              }}
                            >
                              <span
                                style={{
                                  padding: "5px 12px",
                                  borderRadius: "999px",
                                  fontSize: "11px",
                                  fontWeight: 600,
                                  color: "white",
                                  background: getRiskLevelPriorityColors(
                                    item.priority?.toLowerCase() || "medium"
                                  ).gradient,
                                  whiteSpace: "nowrap",
                                }}
                              >
                                {levelDisplayNames[item.priority?.toLowerCase()] || item.priority}
                              </span>
                            </td>
                            <td
                              style={{
                                padding: "14px 16px",
                                textAlign: "center",
                                borderBottom: isLast
                                  ? "none"
                                  : "1px solid rgba(34, 197, 94, 0.1)",
                                borderBottomRightRadius: isLast ? "10px" : "0",
                                verticalAlign: "middle",
                              }}
                            >
                              <span
                                style={{
                                  padding: "4px 10px",
                                  borderRadius: "6px",
                                  fontSize: "12px",
                                  fontWeight: 500,
                                  color: "#3b82f6",
                                  background: "rgba(59, 130, 246, 0.1)",
                                  whiteSpace: "nowrap",
                                }}
                              >
                                {item.estimated_effort}
                              </span>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        </SectionCardAccordion>
      )}

      {/* Section 7: Data Quality Warnings */}
      {data_quality_warnings?.length > 0 && (
        <SectionCardAccordion title={t("dataQualityWarnings")} icon={<i className="fas fa-exclamation-triangle"></i>}>
          <div style={{ overflowX: "auto" }}>
            <table
              style={{
                width: "100%",
                borderCollapse: "separate",
                borderSpacing: "0",
                fontSize: "14px",
              }}
            >
              <thead>
                <tr>
                  <th
                    style={{
                      ...tableHeaderStyle,
                      background: "rgba(245, 158, 11, 0.1)",
                      borderBottom: "1px solid rgba(245, 158, 11, 0.25)",
                      borderTopLeftRadius: "10px",
                    }}
                  >
                    {t("attackType")}
                  </th>
                  <th
                    style={{
                      ...tableHeaderStyle,
                      background: "rgba(245, 158, 11, 0.1)",
                      borderBottom: "1px solid rgba(245, 158, 11, 0.25)",
                    }}
                  >
                    {t("testId")}
                  </th>
                  <th
                    style={{
                      ...tableHeaderStyle,
                      background: "rgba(245, 158, 11, 0.1)",
                      borderBottom: "1px solid rgba(245, 158, 11, 0.25)",
                    }}
                  >
                    {t("issue")}
                  </th>
                  <th
                    style={{
                      ...tableHeaderStyle,
                      background: "rgba(245, 158, 11, 0.1)",
                      borderBottom: "1px solid rgba(245, 158, 11, 0.25)",
                    }}
                  >
                    {t("handling")}
                  </th>
                  <th
                    style={{
                      ...tableHeaderStyle,
                      background: "rgba(245, 158, 11, 0.1)",
                      borderBottom: "1px solid rgba(245, 158, 11, 0.25)",
                      borderTopRightRadius: "10px",
                    }}
                  >
                    {t("rawDetails")}
                  </th>
                </tr>
              </thead>
              <tbody>
                {data_quality_warnings.map((warning, index) => {
                  const isLast = index === data_quality_warnings.length - 1;
                  const cellBorder = isLast ? "none" : "1px solid rgba(245, 158, 11, 0.12)";
                  return (
                    <tr
                      key={warning.test_id || index}
                      style={{
                        background: index % 2 === 0 ? "transparent" : "rgba(245, 158, 11, 0.04)",
                      }}
                    >
                      <td
                        style={{
                          padding: "14px 16px",
                          fontWeight: 600,
                          color: "var(--card-text)",
                          borderBottom: cellBorder,
                          borderBottomLeftRadius: isLast ? "10px" : "0",
                          verticalAlign: "top",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {warning.attack_type || t("notAvailable")}
                      </td>
                      <td
                        style={{
                          padding: "14px 16px",
                          color: "var(--muted-text)",
                          borderBottom: cellBorder,
                          verticalAlign: "top",
                          fontFamily: "monospace",
                          fontSize: "12px",
                          wordBreak: "break-all",
                        }}
                      >
                        {warning.test_id || t("notAvailable")}
                      </td>
                      <td
                        style={{
                          padding: "14px 16px",
                          color: "var(--card-text)",
                          borderBottom: cellBorder,
                          verticalAlign: "top",
                          lineHeight: 1.5,
                        }}
                      >
                        {warning.issue || t("notAvailable")}
                      </td>
                      <td
                        style={{
                          padding: "14px 16px",
                          color: "var(--card-text)",
                          borderBottom: cellBorder,
                          verticalAlign: "top",
                          lineHeight: 1.5,
                        }}
                      >
                        {warning.handling || t("notAvailable")}
                      </td>
                      <td
                        style={{
                          padding: "14px 16px",
                          color: "var(--muted-text)",
                          borderBottom: cellBorder,
                          borderBottomRightRadius: isLast ? "10px" : "0",
                          verticalAlign: "top",
                          lineHeight: 1.5,
                          fontSize: "13px",
                        }}
                      >
                        {warning.raw_details || t("notAvailable")}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </SectionCardAccordion>
      )}

      {/* Section 8: Metadata */}
      {metadata && (
        <SectionCardAccordion title={t("metadata")} icon={<i className="fas fa-info-circle"></i>}>
          <div
            style={{
              display: "flex",
              flexWrap: "wrap",
              alignItems: "center",
              gap: "4px 0",
              fontSize: "14px",
              color: "var(--card-text)",
            }}
          >
            {[
              metadata.report_generated != null && {
                label: t("report"),
                value: new Date(metadata.report_generated).toLocaleString(),
              },
              metadata.total_vulnerabilities_found != null && {
                label: t("vulnerabilities"),
                value: metadata.total_vulnerabilities_found,
              },
              metadata.test_data_source && {
                label: t("dataSource"),
                value: metadata.test_data_source,
              },
              metadata.target_url && {
                label: t("targetUrl"),
                value: metadata.target_url,
              },
              metadata.test_timestamp && {
                label: t("testTime"),
                value: metadata.test_timestamp,
              },
              metadata.session_id && {
                label: t("session"),
                value: metadata.session_id,
              },
              metadata.recommendations_generated != null && {
                label: t("recommendations"),
                value: metadata.recommendations_generated,
              },
              metadata.input_wrapper && {
                label: t("inputWrapper"),
                value: metadata.input_wrapper,
              },
            ]
              .filter(Boolean)
              .map((item, i, arr) => (
                <span key={i} style={{ display: "flex", alignItems: "center", flexWrap: "wrap" }}>
                  <span style={{ color: "var(--muted-text)", fontWeight: 600, marginRight: "4px" }}>
                    {item.label}:
                  </span>
                  <span>{item.value}</span>
                  {i < arr.length - 1 && (
                    <span style={{ margin: "0 10px", color: "var(--muted-text)", opacity: 0.7 }}>•</span>
                  )}
                </span>
              ))}
          </div>
        </SectionCardAccordion>
      )}
    </div>
  );
};

export default McpServerTestsReportTabRecommendations;
